import { useState } from "react";
import { Plus } from "lucide-react";
import { Reveal, SectionHeader } from "./Reveal";
import { PORTFOLIO, PORTFOLIO_CATEGORIES } from "../data/content";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from "../components/ui/dialog";

export const Portfolio = () => {
  const [filter, setFilter] = useState("All");
  const [active, setActive] = useState(null);
  const [activeImage, setActiveImage] = useState(0);

  const visible =
    filter === "All" ? PORTFOLIO : PORTFOLIO.filter((p) => p.category === filter);

  const openProject = (project) => {
    setActive(project);
    setActiveImage(0);
  };

  return (
    <section id="projects" data-testid="portfolio-section" className="border-t border-white/10 bg-ink-2 py-24 md:py-32">
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <SectionHeader eyebrow="Our Work" title="Homes We're Proud to Stand Behind" />

        <div className="mt-12 flex flex-wrap gap-2" role="group" aria-label="Filter projects by category">
          {PORTFOLIO_CATEGORIES.map((category) => (
            <button
              key={category}
              onClick={() => setFilter(category)}
              data-testid={`portfolio-filter-${category.toLowerCase().replace(/\s+/g, "-")}`}
              aria-pressed={filter === category}
              className={`border px-5 py-2.5 font-accent text-[11px] font-semibold uppercase tracking-[0.2em] transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-gold ${
                filter === category
                  ? "border-gold bg-gold text-black"
                  : "border-white/15 text-gray-400 hover:border-gold/60 hover:text-gold"
              }`}
            >
              {category}
            </button>
          ))}
        </div>

        <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {visible.map((project, i) => (
            <Reveal key={project.title} delay={0.06 * i} className={i % 5 === 0 ? "sm:col-span-2 lg:col-span-2" : ""}>
              <button
                onClick={() => openProject(project)}
                data-testid={`portfolio-item-${i}`}
                aria-label={`Open gallery for ${project.title}`}
                className="group relative block w-full overflow-hidden text-left focus:outline-none focus:ring-2 focus:ring-gold"
              >
                <div className={`${i % 5 === 0 ? "aspect-[21/10]" : "aspect-[4/3]"} overflow-hidden`}>
                  <img
                    src={project.image}
                    alt={project.title}
                    loading="lazy"
                    className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.05]"
                  />
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/20 to-transparent" aria-hidden="true" />
                <span
                  className="absolute right-5 top-5 flex h-10 w-10 items-center justify-center border border-white/20 bg-black/40 text-white opacity-0 backdrop-blur-sm transition-all duration-300 group-hover:opacity-100"
                  aria-hidden="true"
                >
                  <Plus className="h-4 w-4" />
                </span>
                <div className="absolute inset-x-0 bottom-0 p-6">
                  <span className="font-accent text-[10px] font-semibold uppercase tracking-[0.25em] text-gold">
                    {project.category}
                  </span>
                  <h3 className="mt-2 font-serif text-2xl font-medium text-white">{project.title}</h3>
                  <p className="mt-1 text-xs uppercase tracking-[0.2em] text-gray-400">{project.location}</p>
                </div>
              </button>
            </Reveal>
          ))}
        </div>
      </div>

      <Dialog open={!!active} onOpenChange={(open) => !open && setActive(null)}>
        <DialogContent
          className="max-w-5xl border-white/10 bg-ink p-0 sm:rounded-none"
          data-testid="portfolio-gallery-dialog"
        >
          {active && (
            <div className="p-6 md:p-10">
              <DialogTitle className="font-serif text-3xl font-medium text-white">{active.title}</DialogTitle>
              <DialogDescription className="mt-2 font-accent text-[11px] font-medium uppercase tracking-[0.25em] text-gold">
                {active.category} — {active.location}
              </DialogDescription>
              <div className="mt-8 overflow-hidden">
                <img
                  src={active.gallery[activeImage]}
                  alt={`${active.title} photo ${activeImage + 1}`}
                  className="aspect-[16/9] w-full object-cover"
                  data-testid="portfolio-gallery-main-image"
                />
              </div>
              <div className="mt-4 grid grid-cols-3 gap-4">
                {active.gallery.map((src, idx) => (
                  <button
                    key={src}
                    onClick={() => setActiveImage(idx)}
                    data-testid={`portfolio-gallery-thumb-${idx}`}
                    aria-label={`View photo ${idx + 1} of ${active.title}`}
                    aria-pressed={activeImage === idx}
                    className={`overflow-hidden border transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-gold ${
                      activeImage === idx ? "border-gold" : "border-white/10 hover:border-white/40"
                    }`}
                  >
                    <img src={src} alt="" className="aspect-[16/10] w-full object-cover" />
                  </button>
                ))}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
};
